import * as React from 'react';
import { cn } from '../lib/cn';

// `variant` is the shadcn naming the three SPAs were written against; `tone` is this package's
// own vocabulary (matching Badge). Both are accepted so a call site can use either — `error` and
// `destructive` are the same toast.
export type ToastTone = 'default' | 'success' | 'error';
export type ToastVariant = 'default' | 'success' | 'destructive';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

/**
 * The options a caller passes to `toast(...)`. A bare string is also accepted as shorthand for
 * `{ title: <string> }`, which keeps the old `toast('done', 'success')` positional form working.
 */
export interface ToastOptions {
  title: string;
  description?: string;
  /** shadcn spelling. `destructive` maps to the danger tone. */
  variant?: ToastVariant;
  /** this package's spelling; wins over `variant` if both are given. */
  tone?: ToastTone;
  /** Auto-dismiss after N ms. Defaults to 5000; an action bumps the default to 8000. */
  duration?: number;
  /** A single affordance rendered as a button — the ADM-3 "undo" case. */
  action?: ToastAction;
}

export interface Toast {
  id: number;
  title: string;
  description?: string;
  tone: ToastTone;
  action?: ToastAction;
}

interface ToastContextValue {
  /**
   * `toast({ title, ... })` is the primary form. `toast('message', 'success')` is kept as a
   * positional shorthand so the minimal call sites did not have to change.
   */
  toast: (opts: ToastOptions | string, tone?: ToastTone) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

function variantToTone(variant: ToastVariant | undefined): ToastTone {
  if (variant === 'destructive') return 'error';
  if (variant === 'success') return 'success';
  return 'default';
}

let nextId = 1;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  const toast = React.useCallback((opts: ToastOptions | string, positionalTone?: ToastTone) => {
    const o: ToastOptions =
      typeof opts === 'string' ? { title: opts, tone: positionalTone } : opts;
    const tone = o.tone ?? variantToTone(o.variant);
    const id = nextId++;
    const entry: Toast = {
      id,
      title: o.title,
      description: o.description,
      tone,
      action: o.action,
    };
    setToasts((current) => [...current, entry]);
    const duration = o.duration ?? (o.action ? 8000 : 5000);
    setTimeout(() => setToasts((current) => current.filter((t) => t.id !== id)), duration);
  }, []);

  const value = React.useMemo(() => ({ toast }), [toast]);

  const dismiss = React.useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Polite, not assertive: a toast must not interrupt a screen reader mid-sentence. */}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed bottom-4 left-1/2 z-[60] flex w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 flex-col gap-2 sm:bottom-6 sm:left-auto sm:right-6 sm:translate-x-0"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            // Toned like a Badge: the `status-*` token as the text and its own /30 as the hairline.
            //
            // The tint is a `background-image` layer, not a `bg-*` color, and that is deliberate.
            // A toast is `fixed` over arbitrary page content, so a bare 10%-alpha fill would let a
            // table row show through the message — it needs an opaque base underneath. But `cn` is
            // tailwind-merge, which collapses two `bg-<color>` classes down to the last one, so
            // `bg-background bg-status-success/10` would silently drop the base. Painting the tint
            // as a flat gradient puts it in a different utility group, so both survive and it
            // composites over the opaque surface the way it does on an in-flow panel.
            className={cn(
              'pointer-events-auto rounded-md border bg-background px-4 py-3 text-sm shadow-lg',
              t.tone === 'success' &&
                'border-status-success/30 bg-[linear-gradient(hsl(var(--status-success)/0.1),hsl(var(--status-success)/0.1))] text-status-success',
              t.tone === 'error' &&
                'border-status-danger/30 bg-[linear-gradient(hsl(var(--status-danger)/0.1),hsl(var(--status-danger)/0.1))] text-status-danger',
              t.tone === 'default' && 'border-border text-foreground',
            )}
          >
            <div className="font-medium">{t.title}</div>
            {t.description ? (
              <div className="mt-0.5 text-xs text-muted-foreground">{t.description}</div>
            ) : null}
            {t.action ? (
              <button
                type="button"
                className="mt-2 text-xs font-semibold underline underline-offset-2 hover:no-underline"
                onClick={() => {
                  t.action?.onClick();
                  dismiss(t.id);
                }}
              >
                {t.action.label}
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
